import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, X, UserPlus } from 'lucide-react';
import {
  lookupUserForPassList,
  type PassListUserRow,
} from '@/lib/api';
import formStyles from '@/components/Form.module.css';
import styles from './UserTypeahead.module.css';

/**
 * Exact-match user picker for the resident's "Guest pass list".
 *
 * THIS WAS A TYPEAHEAD UNTIL 2026-08-05 AND MUST NOT BECOME ONE AGAIN.
 * The old version searched on every keystroke and rendered each match's
 * full email address and real name. Typing "Warr" returned five
 * strangers' accounts — any driver with a charger could harvest the
 * tenant directory a few letters at a time. There is now no partial
 * matching on either side: the host types a COMPLETE email address or
 * a COMPLETE phone number, presses Add, and the server resolves one
 * account or none.
 *
 * Consequences of that shape, all deliberate:
 *   - No suggestion list, so nothing renders another user's details
 *     before the host has proven they already know the identifier.
 *   - Lookup runs on submit, not on keystroke — no debounce, and no
 *     request per character.
 *   - A resolved person is added immediately; there is no intermediate
 *     "pick from results" step, because there is only ever one result.
 *
 * Chips show the person: username, else name, else the email. They are
 * seeded from `knownMembers` (the server hydrates the charger's
 * existing pass list) so a freshly-loaded page never renders an
 * ObjectId, and augmented by each successful lookup.
 */

export interface UserTypeaheadProps {
  /** ObjectId strings of currently-selected users. */
  selectedUserIds: string[];
  /** Caller persists the next selection. */
  onChange: (ids: string[]) => void;
  /** Cap on selection size. Undefined = unlimited. */
  maxSelections?: number;
  placeholder?: string;
  disabled?: boolean;
  /** Suppress these IDs — already-added members elsewhere in the UI. */
  excludeUserIds?: string[];
  /** Optional accessibility label for the input. */
  ariaLabel?: string;
  /** When provided, the server pre-excludes this charger's existing
   *  pass-list members from the lookup result. */
  chargerId?: string;
  /** Label for the cross-tenant pill. Default reads for multifamily;
   *  a home passes residential copy (slice D). */
  crossTenantLabel?: string;
  /** Identities for already-selected ids, from the charger payload's
   *  guest_pass_list_members. Without this, chips for server-loaded
   *  members have no name to show. */
  knownMembers?: PassListUserRow[];
}

type UserCache = Map<string, PassListUserRow>;

/** Person-facing label for a resolved user. Never an id. */
function labelFor(u: PassListUserRow): string {
  return u.username ?? u.name ?? u.email;
}

export function UserTypeahead({
  selectedUserIds,
  onChange,
  maxSelections,
  placeholder,
  disabled = false,
  excludeUserIds,
  ariaLabel,
  chargerId,
  crossTenantLabel,
  knownMembers,
}: UserTypeaheadProps) {
  const { t } = useTranslation('typeaheads');
  const crossTenantText = crossTenantLabel ?? t('user.crossTenantDefault');
  const [query, setQuery] = useState('');
  const [looking, setLooking] = useState(false);
  const [notice, setNotice] = useState<
    null | { kind: 'incomplete' | 'ambiguous' | 'notFound' | 'already' | 'error' }
  >(null);
  const [userCache, setUserCache] = useState<UserCache>(() => new Map());
  const reqSeq = useRef(0);

  // Seed the cache from server-hydrated members so existing chips
  // render a person on first paint.
  useEffect(() => {
    if (!knownMembers?.length) return;
    setUserCache((prev) => {
      const next = new Map(prev);
      for (const m of knownMembers) next.set(m._id, m);
      return next;
    });
  }, [knownMembers]);

  const atCapacity = typeof maxSelections === 'number'
    && selectedUserIds.length >= maxSelections;
  const inputDisabled = disabled || atCapacity;

  const exclusionSet = useMemo(() => {
    const set = new Set<string>();
    for (const id of selectedUserIds) set.add(id);
    if (excludeUserIds) for (const id of excludeUserIds) set.add(id);
    return set;
  }, [selectedUserIds, excludeUserIds]);

  const submitLookup = (): void => {
    const term = query.trim();
    if (!term || inputDisabled || looking) return;
    const seq = ++reqSeq.current;
    setLooking(true);
    setNotice(null);
    void lookupUserForPassList(term, chargerId ? { chargerId } : undefined)
      .then((res) => {
        // Ignore a stale response if the host submitted again.
        if (seq !== reqSeq.current) return;
        if (res.incomplete) { setNotice({ kind: 'incomplete' }); return; }
        if (res.ambiguous) { setNotice({ kind: 'ambiguous' }); return; }
        if (!res.user) { setNotice({ kind: 'notFound' }); return; }
        if (exclusionSet.has(res.user._id)) { setNotice({ kind: 'already' }); return; }
        const user = res.user;
        setUserCache((prev) => {
          const next = new Map(prev);
          next.set(user._id, user);
          return next;
        });
        onChange([...selectedUserIds, user._id]);
        setQuery('');
      })
      .catch(() => {
        if (seq === reqSeq.current) setNotice({ kind: 'error' });
      })
      .finally(() => {
        if (seq === reqSeq.current) setLooking(false);
      });
  };

  const handleRemove = (id: string): void => {
    onChange(selectedUserIds.filter((x) => x !== id));
  };

  const renderChip = (id: string): React.ReactNode => {
    const cached = userCache.get(id);
    // Never render a raw ObjectId. An id with no identity means the
    // User doc is gone (deleted account) — say so rather than showing
    // hex the host cannot act on.
    const label = cached ? labelFor(cached) : t('user.unknownMember');
    const isCross = cached?.is_cross_tenant === true;
    return (
      <span key={id} className={styles.chip}>
        <span className={styles.chipText}>{label}</span>
        {isCross && (
          <span className={styles.crossTenantPill} aria-label={crossTenantText}>
            {crossTenantText}
          </span>
        )}
        <button
          type="button"
          className={styles.chipClear}
          onClick={() => handleRemove(id)}
          disabled={disabled}
          aria-label={t('user.remove', { label })}
        >
          <X size={12} />
        </button>
      </span>
    );
  };

  return (
    <div className={styles.wrap}>
      {selectedUserIds.length > 0 && (
        <div className={styles.chipRow}>
          {selectedUserIds.map(renderChip)}
        </div>
      )}

      {typeof maxSelections === 'number' && (
        <div className={styles.counterRow}>
          <span className={styles.counter}>
            {t('user.counter', { current: selectedUserIds.length, max: maxSelections })}
          </span>
        </div>
      )}

      <div className={styles.lookupRow}>
        <input
          className={formStyles.input}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setNotice(null); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); submitLookup(); }
          }}
          placeholder={atCapacity
            ? t('user.maxReached', { max: maxSelections })
            : (placeholder ?? t('user.exactPlaceholder'))}
          disabled={inputDisabled}
          autoComplete="off"
          inputMode="email"
          aria-label={ariaLabel ?? t('user.exactAria')}
        />
        <button
          type="button"
          className={styles.addButton}
          onClick={submitLookup}
          disabled={inputDisabled || looking || !query.trim()}
          aria-label={t('user.addAria')}
        >
          {looking
            ? <Loader2 size={14} className={styles.spin} />
            : <UserPlus size={14} />}
          <span>{t('user.add')}</span>
        </button>
      </div>

      <p className={styles.lookupHint}>{t('user.exactHint')}</p>

      {notice && (
        <p className={styles.lookupNotice} role="status">
          {t(`user.notice.${notice.kind}`)}
        </p>
      )}
    </div>
  );
}
